import { CircleDashedIcon, CircleWithCheckIcon, ErrorIcon } from '#icons';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { FC, useMemo } from 'react';

import { BaseProps, createSxProps } from '../types';

export type ProgressQueueItemStatus = 'pending' | 'completed' | 'error';

export interface ProgressQueueItem {
  id: string;
  title: string;
  description?: string | null;
  status: ProgressQueueItemStatus;
}

export interface ProgressQueueProps extends BaseProps {
  items: ProgressQueueItem[];
  sx?: SxProps<Theme>;
}

const useSxProps = () => {
  return useMemo(() => {
    return createSxProps({
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      },
      row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        minWidth: 0,
      },
      content: {
        minWidth: 0,
        flex: 1,
      },
    });
  }, []);
};

const getStatusIcon = (status: ProgressQueueItemStatus): JSX.Element => {
  switch (status) {
    case 'completed':
      return <CircleWithCheckIcon fontSize="small" color="success" />;
    case 'error':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'pending':
    default:
      return <CircleDashedIcon fontSize="small" color="disabled" />;
  }
};

const getItemTitleSx = (isCompleted: boolean, isError: boolean): SxProps<Theme> => [
  {
    textDecoration: isCompleted ? 'line-through' : 'none',
    wordBreak: 'break-word',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  isError ? { color: 'error.main' } : isCompleted ? { color: 'text.secondary' } : {},
];

const getItemDescriptionSx = (isCompleted: boolean, isError: boolean): SxProps<Theme> => [
  {
    display: 'block',
    mt: 0.25,
    wordBreak: 'break-word',
    textDecoration: isCompleted ? 'line-through' : 'none',
  },
  isError ? { color: 'error.main' } : {},
];

export const ProgressQueue: FC<ProgressQueueProps> = ({ items, dataTestId, sx }) => {
  const sxProps = useSxProps();

  if (!items.length) {
    return null;
  }

  return (
    <Box
      data-testid={dataTestId}
      // eslint-disable-next-line no-restricted-syntax
      sx={{
        ...sxProps.root,
        ...sx,
      }}
    >
      {items.map((item) => {
        const isCompleted = item.status === 'completed';
        const isError = item.status === 'error';

        return (
          <Box
            key={item.id}
            // eslint-disable-next-line no-restricted-syntax
            sx={sxProps.row}
          >
            {getStatusIcon(item.status)}
            <Box
              // eslint-disable-next-line no-restricted-syntax
              sx={sxProps.content}
            >
              <Typography
                variant="body2"
                color="text.primary"
                // eslint-disable-next-line no-restricted-syntax
                sx={getItemTitleSx(isCompleted, isError)}
              >
                {item.title}
              </Typography>
              {item.description ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  // eslint-disable-next-line no-restricted-syntax
                  sx={getItemDescriptionSx(isCompleted, isError)}
                >
                  {item.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProgressQueue;
